import React, { useState } from 'react'
import { get, isNil, isEmpty, uniq } from 'lodash'
import { IoIosFunnel, IoIosInformationCircleOutline } from 'react-icons/io'
import BusinessRule from '../../interfaces/BusinessRule'
import Column from '../../interfaces/Column'
import CapitalizeValueDisplay from '../common/CapitalizeValueDisplay'
import IndexFilterModal from '../common/IndexFilterModal'

interface Props {
  records: Array<BusinessRule>,
  columns: Array<Column>,
  filtered: boolean,
  handelRulePriorityModal?: (event: React.MouseEvent, ruleType: string) => void
}

const SharedBusinessRulesTable: React.FC<Props> = ({
  records,
  columns,
  filtered,
  handelRulePriorityModal
}) => {
  const [columnFilters, setColumnFilters] = useState<{ [name: string]: string }>({})
  const [columnFiltered, setColumnFiltered] = useState<Column>(
    { key: '', label: '', display: () => null, filterable: false }
  )
  const [displayFilterSelection, setDisplayFilterSelection] = useState(false)

  const headerCell = (column: Column) => {
    return (
      <th key={`header-cell-${column.key}`} className="header-cell rule-table__header-cell">
        <div>
          <div className="header-label">{column.label}</div>
          {filtered
                && (
                  <div className="header-actions">
                    {filterButton(column)}
                  </div>
                )}
        </div>
      </th>
    )
  }

  const filterModal = () => {
    if (isEmpty(columnFiltered) || columnFiltered.filterable !== true) {
      return null
    }

    if (displayFilterSelection !== true) {
      return null
    }

    const filteredValues = uniq(
      records.map((record: BusinessRule) => {
        type ObjectKey = keyof typeof record

        return CapitalizeValueDisplay(record[columnFiltered.key as ObjectKey] as string)
      })
    )

    const options = { records: filteredValues, type: 'standardSelectField' }

    return (
      <IndexFilterModal
        label={columnFiltered.label}
        filterValues={options}
        handleClose={handleFilterClose}
        handleSelected={handleFilterSelected}
        initialValue={columnFilters[columnFiltered.key]}
        remoteKey={columnFiltered.key}
      />
    )
  }

  const handleFilterClose = () => {
    setDisplayFilterSelection(false)
  }
  const handleFilterSelected = (name: string, value: string) => {
    let newFilter: { [name: string]: string }
    if (!isNil(value) && !isEmpty(value)) {
      newFilter = { ...columnFilters, [name]: value }
    } else {
      newFilter = { ...columnFilters }
      delete newFilter[name]
    }
    setColumnFilters(newFilter)

    setDisplayFilterSelection(false)
  }

  const isFiltered = (column: Column) => {
    return !isEmpty(columnFilters) && !isNil(columnFilters[column.key])
  }

  const filterButton = (column: Column) => {
    if (column.filterable !== true) {
      return null
    }

    if (isFiltered(column)) {
      return updateFilterButton(column)
    }
    return enableFilterButton(column)
  }

  const updateFilterButton = (column: Column) => {
    return (
      <button
        type="button"
        onClick={(event) => handleUpdateFilterButton(event, column)}
        className="current hidden-button remove-filter-button"
      >
        {filterText(column)}
        <i><IoIosFunnel /></i>
      </button>
    )
  }

  const filterText = (column: Column) => {
    const count = columnFilters[column.key].length

    return `${count} Selected`
  }

  const handleUpdateFilterButton = (event: React.MouseEvent<HTMLButtonElement>, column: Column) => {
    event.preventDefault()
    setDisplayFilterSelection(true)
    setColumnFiltered(column)
  }

  const enableFilterButton = (column: Column) => {
    return (
      <button
        name={column.key}
        type="button"
        onClick={(event) => handleFilterButton(event, column)}
        className="hidden-button filter-button"
      >
        Filter
        <i><IoIosFunnel /></i>
      </button>
    )
  }

  const handleFilterButton = (event: React.MouseEvent<HTMLButtonElement>, column: Column) => {
    event.preventDefault()

    setDisplayFilterSelection(true)
    setColumnFiltered(column)
  }

  const tableHeader = () => {
    return (
      <thead>
        <tr>
          {columns.map((column) => headerCell(column as Column))}
        </tr>
      </thead>
    )
  }

  const rows = (record: BusinessRule) => {
    return (
      <tr key={`row-${record.id}`}>
        {columns.map((column) => valueCell(record, column as Column))}
      </tr>
    )
  }

  const defaultDisplay = (record: BusinessRule, column: Column) => {
    return get(record, column.key)
  }

  const valueCell = (record: BusinessRule, column: Column) => {
    let displayValue
    if (!isNil(column.display)) {
      displayValue = column.display(record, column)
    } else {
      displayValue = defaultDisplay(record, column)
    }

    const handleClick = (event: React.MouseEvent) => {
      handelRulePriorityModal?.(event, record.shared_business_rule_type)
      event.preventDefault()
    }

    if (column.key === 'shared_business_rule_type') {
      displayValue = (
        <i title="Business Rule Priority" className="business_rule_priority_icon">
          {displayValue}
          <IoIosInformationCircleOutline
            className="priority_button"
            size={20}
            onClick={handleClick}
          />
        </i>
      )
    }

    const safeKey = `${column.key}`.toLowerCase().replace(/[^a-z]+/g, '-')
    return (
      <td
        key={`row-cell-${safeKey}`}
        className={`rule-table__row-cell__${safeKey} rule-table__row-cell`}
      >{displayValue}
      </td>
    )
  }

  const isColumnFiltered = (columnName: string, record: BusinessRule) => {
    type ObjectKey = keyof typeof record

    return (columnName in columnFilters
            && columnFilters[columnName].includes(CapitalizeValueDisplay(record[columnName as ObjectKey])))
  }

  const businessRulesTable = () => {
    const filteredRecords: Array<BusinessRule> = records.filter((record: BusinessRule) => isEmpty(columnFilters)
                                                              || isColumnFiltered('matching_type', record)
                                                              || isColumnFiltered('shared_business_rule_type', record))
    return (
      <div>
        <div key="businessRulesTable">
          <div className="full-width-table-container">
            <table className="pure-table full-width-table rule-table">
              {tableHeader()}
              <tbody>
                {filteredRecords.map((record) => rows(record))}
              </tbody>
            </table>
          </div>
        </div>
        {filterModal()}
      </div>
    )
  }

  return (businessRulesTable())
}

export default SharedBusinessRulesTable
